<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

// ── Connection status ──────────────────────────────────────────────────
const connectionStatus = computed(() => {
  const conn = props.inbox.provider_connection?.connection;
  if (conn === 'open') return 'connected';
  if (conn === 'connecting' || conn === 'reconnecting') return 'connecting';
  if (conn === 'close') return 'failed';
  return props.inbox.provider_config?.connection_status || 'waiting';
});
const baileysQrCode = computed(
  () => props.inbox.provider_connection?.qr_data_url || null
);
const phoneNumber = computed(() => props.inbox.phone_number || '');
const inboxName = computed(() => props.inbox.name || '');

const statusColor = computed(() => {
  const map = {
    connected: 'bg-green-500',
    connecting: 'bg-yellow-400',
    waiting: 'bg-n-slate-5',
    failed: 'bg-red-500',
  };
  return map[connectionStatus.value] || 'bg-n-slate-5';
});

const statusLabel = computed(() => {
  const map = {
    connected: 'Conectado',
    connecting: 'Conectando...',
    waiting: 'Aguardando',
    failed: 'Falha na conexão',
  };
  return map[connectionStatus.value] || 'Aguardando';
});

// ── Modal de conexão ───────────────────────────────────────────────────
const showConnectionModal = ref(false);
const isRequestingQr = ref(false);
const qrError = ref(false);
const qrErrorMessage = ref('');
const isDisconnecting = ref(false);
let qrTimeoutId = null;
let pollIntervalId = null;

const accountId = computed(() => store.getters.getCurrentAccountId);

const clearQrTimeout = () => {
  if (qrTimeoutId) {
    clearTimeout(qrTimeoutId);
    qrTimeoutId = null;
  }
};

const stopPolling = () => {
  if (pollIntervalId) {
    clearInterval(pollIntervalId);
    pollIntervalId = null;
  }
};

// Reads this inbox straight from the API. `inboxes/get` serves the cached list, and a
// connection change deliberately does not invalidate that cache — so it never brought
// the QR, which then only showed up if the ActionCable event happened to arrive.
const refreshProviderConnection = async () => {
  try {
    const { data } = await window.axios.get(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}`
    );
    await store.dispatch('inboxes/updateProviderConnection', {
      id: props.inbox.id,
      providerConnection: data.provider_connection,
    });
  } catch {
    // the next tick (or the ActionCable event) tries again
  }
};

const startPolling = () => {
  stopPolling();
  // Keeps polling while the QR is visible: Baileys rotates it every ~20s.
  pollIntervalId = setInterval(async () => {
    if (connectionStatus.value === 'connected') {
      stopPolling();
      return;
    }
    await refreshProviderConnection();
  }, 3000);
};

const requestQrCode = async () => {
  isRequestingQr.value = true;
  qrError.value = false;
  qrErrorMessage.value = '';
  clearQrTimeout();
  stopPolling();
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/generate_baileys_qr`
    );
    startPolling();
    qrTimeoutId = setTimeout(() => {
      stopPolling();
      if (connectionStatus.value !== 'connected') {
        qrError.value = true;
        qrErrorMessage.value =
          'O serviço Baileys não respondeu a tempo. Verifique se o serviço está ativo e tente novamente.';
      }
    }, 180000);
  } catch (error) {
    qrError.value = true;
    qrErrorMessage.value =
      error?.response?.data?.error ||
      'Falha ao solicitar QR Code. Verifique se o serviço Baileys está acessível.';
  } finally {
    isRequestingQr.value = false;
  }
};

const disconnect = async () => {
  isDisconnecting.value = true;
  try {
    await window.axios.delete(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/disconnect_baileys`
    );
    await store.dispatch('inboxes/get');
    showConnectionModal.value = false;
    useAlert('Dispositivo desconectado com sucesso.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error || 'Erro ao desconectar o dispositivo.'
    );
  } finally {
    isDisconnecting.value = false;
  }
};

const openConnectionModal = () => {
  qrError.value = false;
  qrErrorMessage.value = '';
  showConnectionModal.value = true;
  // If not connected, request QR automatically
  if (connectionStatus.value !== 'connected') {
    requestQrCode();
  }
};

watch(baileysQrCode, newVal => {
  if (newVal) {
    clearQrTimeout();
    qrError.value = false;
    // mantém polling ativo para detectar quando a conexão for confirmada
  }
});
watch(connectionStatus, newVal => {
  if (newVal === 'connected') {
    clearQrTimeout();
    stopPolling();
    qrError.value = false;
  }
});

onBeforeUnmount(() => {
  clearQrTimeout();
  stopPolling();
});

// ── URL do provedor ────────────────────────────────────────────────────
const providerUrl = ref(props.inbox.provider_config?.api_url || '');
const isUpdatingUrl = ref(false);

const updateProviderUrl = async () => {
  isUpdatingUrl.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      channel: {
        provider_config: {
          ...props.inbox.provider_config,
          api_url: providerUrl.value,
        },
      },
    });
    useAlert('URL do provedor atualizada com sucesso.');
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar a URL do provedor.');
  } finally {
    isUpdatingUrl.value = false;
  }
};

// ── Chave de API ───────────────────────────────────────────────────────
const apiKey = ref('');
const isUpdatingKey = ref(false);

const updateApiKey = async () => {
  if (!apiKey.value.trim()) return;
  isUpdatingKey.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      channel: {
        provider_config: {
          ...props.inbox.provider_config,
          api_key: apiKey.value.trim(),
        },
      },
    });
    apiKey.value = '';
    useAlert('Chave de API atualizada com sucesso.');
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar a chave de API.');
  } finally {
    isUpdatingKey.value = false;
  }
};

// ── Confirmações de leitura ────────────────────────────────────────────
const sendReadReceipts = ref(
  props.inbox.provider_config?.send_read_receipts !== false
);
const isUpdatingReceipts = ref(false);

const toggleReadReceipts = async () => {
  isUpdatingReceipts.value = true;
  const newValue = !sendReadReceipts.value;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      channel: {
        provider_config: {
          ...props.inbox.provider_config,
          send_read_receipts: newValue,
        },
      },
    });
    sendReadReceipts.value = newValue;
    useAlert(
      newValue
        ? 'Confirmações de leitura ativadas.'
        : 'Confirmações de leitura desativadas.'
    );
  } catch (error) {
    useAlert(error?.message || 'Erro ao atualizar configuração.');
  } finally {
    isUpdatingReceipts.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-0">
    <!-- ── Gerenciar Conexão ── -->
    <div class="flex items-start justify-between py-6 border-b border-n-weak">
      <div class="flex flex-col gap-1 max-w-xs">
        <span class="text-n-blue-9 font-semibold text-sm">{{
          t('BAILEYS.MANAGE_CONNECTION_TITLE')
        }}</span>
        <p class="text-xs text-n-slate-11">
          {{ t('BAILEYS.MANAGE_CONNECTION_SUBTITLE') }}
        </p>
      </div>
      <div class="flex flex-col items-center gap-3 min-w-[14rem]">
        <!-- Status row -->
        <div
          class="flex items-center justify-center gap-2 text-sm text-n-slate-12 w-full"
        >
          <span>{{ '📱 ' }}{{ inboxName }}</span>
          <span class="font-semibold">{{ phoneNumber }}</span>
          <span
            class="w-2.5 h-2.5 rounded-full flex-shrink-0"
            :class="[statusColor]"
            :title="statusLabel"
          />
        </div>
        <NextButton
          solid
          blue
          :label="t('BAILEYS.MANAGE_CONNECTION_BUTTON')"
          @click="openConnectionModal"
        />
      </div>
    </div>

    <!-- ── URL do Provedor ── -->
    <div class="flex items-start justify-between py-6 border-b border-n-weak">
      <div class="flex flex-col gap-1 max-w-xs">
        <span class="text-n-blue-9 font-semibold text-sm">{{
          t('BAILEYS.PROVIDER_URL_TITLE')
        }}</span>
        <p class="text-xs text-n-slate-11">
          {{ t('BAILEYS.PROVIDER_URL_SUBTITLE') }}
        </p>
      </div>
      <div class="flex items-center gap-2 min-w-[18rem]">
        <input
          v-model="providerUrl"
          type="text"
          class="!mb-0 flex-1"
          :placeholder="t('BAILEYS.PROVIDER_URL_PLACEHOLDER')"
        />
        <NextButton
          solid
          blue
          :is-loading="isUpdatingUrl"
          :label="t('BAILEYS.UPDATE_BUTTON')"
          @click="updateProviderUrl"
        />
      </div>
    </div>

    <!-- ── Chave de API ── -->
    <div class="flex items-start justify-between py-6 border-b border-n-weak">
      <div class="flex flex-col gap-1 max-w-xs">
        <span class="text-n-blue-9 font-semibold text-sm">{{
          t('BAILEYS.API_KEY_TITLE')
        }}</span>
        <p class="text-xs text-n-slate-11">
          {{ t('BAILEYS.API_KEY_SUBTITLE') }}
        </p>
      </div>
      <div class="flex items-center gap-2 min-w-[18rem]">
        <input
          v-model="apiKey"
          type="password"
          class="!mb-0 flex-1"
          :placeholder="t('BAILEYS.API_KEY_PLACEHOLDER')"
        />
        <NextButton
          solid
          blue
          :is-loading="isUpdatingKey"
          :disabled="!apiKey.trim()"
          :label="t('BAILEYS.UPDATE_BUTTON')"
          @click="updateApiKey"
        />
      </div>
    </div>

    <!-- ── Confirmações de leitura ── -->
    <div class="flex items-start justify-between py-6">
      <div class="flex flex-col gap-1 max-w-xs">
        <span class="text-n-blue-9 font-semibold text-sm">{{
          t('BAILEYS.READ_RECEIPTS_TITLE')
        }}</span>
        <p class="text-xs text-n-slate-11">
          {{ t('BAILEYS.READ_RECEIPTS_SUBTITLE') }}
        </p>
      </div>
      <div class="flex items-center gap-3">
        <span class="text-sm text-n-slate-11">{{
          t('BAILEYS.READ_RECEIPTS_TOGGLE_LABEL')
        }}</span>
        <button
          :disabled="isUpdatingReceipts"
          class="relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none"
          :class="sendReadReceipts ? 'bg-n-blue-9' : 'bg-n-slate-5'"
          @click="toggleReadReceipts"
        >
          <span
            class="inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform"
            :class="sendReadReceipts ? 'translate-x-6' : 'translate-x-1'"
          />
        </button>
      </div>
    </div>

    <!-- ── Modal de Conexão ── -->
    <woot-modal
      v-if="showConnectionModal"
      :show="showConnectionModal"
      :close-on-backdrop-click="false"
      :on-close="
        () => {
          showConnectionModal = false;
          clearQrTimeout();
          stopPolling();
        }
      "
    >
      <template #default>
        <div class="flex flex-col gap-0 min-w-[26rem] max-w-[32rem]">
          <!-- Header -->
          <div class="px-6 pt-6 pb-4 border-b border-n-weak">
            <h3 class="text-heading-2 font-bold text-n-slate-12 mb-1">
              {{ t('BAILEYS.MODAL_TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ t('BAILEYS.MODAL_SUBTITLE') }}
            </p>
          </div>

          <!-- Body -->
          <div class="px-6 py-6 flex flex-col items-center gap-5">
            <!-- Status badge -->
            <div
              class="flex items-center gap-2 bg-n-surface-2 rounded-xl px-4 py-2"
            >
              <span class="text-sm text-n-slate-12"
                >{{ '📱 ' }}{{ inboxName }}</span
              >
              <span class="text-sm font-semibold text-n-slate-12">{{
                phoneNumber
              }}</span>
              <span
                class="w-2.5 h-2.5 rounded-full flex-shrink-0"
                :class="[statusColor]"
                :title="statusLabel"
              />
            </div>

            <!-- Connected state -->
            <div
              v-if="connectionStatus === 'connected'"
              class="flex flex-col items-center gap-4 w-full"
            >
              <div class="flex flex-col items-center gap-2">
                <span class="text-4xl">{{ '✅' }}</span>
                <p class="text-base font-semibold text-green-600">
                  {{ t('BAILEYS.DEVICE_CONNECTED_TITLE') }}
                </p>
                <p class="text-xs text-n-slate-10 text-center">
                  {{ t('BAILEYS.DEVICE_CONNECTED_SUBTITLE') }}
                </p>
              </div>
              <div
                class="w-full pt-2 border-t border-n-weak flex justify-center"
              >
                <NextButton
                  solid
                  ruby
                  :is-loading="isDisconnecting"
                  :label="t('BAILEYS.DISCONNECT_BUTTON')"
                  @click="disconnect"
                />
              </div>
            </div>

            <!-- QR Code visible -->
            <div
              v-else-if="baileysQrCode"
              class="flex flex-col items-center gap-4"
            >
              <p class="text-xs text-n-slate-10 text-center">
                {{ t('BAILEYS.OPEN_WHATSAPP_INSTRUCTION') }}
                <strong>{{ t('BAILEYS.SCAN_QR_CONNECTED_DEVICES') }}</strong>
                <strong>{{ t('BAILEYS.SCAN_QR_LINK_DEVICE') }}</strong>
              </p>
              <div
                class="rounded-xl shadow-md outline outline-1 outline-n-weak p-3 bg-white"
              >
                <img
                  :src="baileysQrCode"
                  alt="WhatsApp QR Code"
                  class="rounded-lg size-56"
                />
              </div>
              <p class="text-xs font-semibold text-n-brand-500 animate-pulse">
                {{ t('BAILEYS.WAITING_QR_READ') }}
              </p>
            </div>

            <!-- Error state -->
            <div
              v-else-if="qrError"
              class="flex flex-col items-center gap-4 py-2"
            >
              <span class="text-4xl">{{ t('BAILEYS.WARNING_ICON') }}</span>
              <p
                class="text-sm font-semibold text-red-600 text-center max-w-xs"
              >
                {{ qrErrorMessage }}
              </p>
              <NextButton
                solid
                blue
                :is-loading="isRequestingQr"
                :label="t('BAILEYS.RETRY')"
                @click="requestQrCode"
              />
            </div>

            <!-- Loading / requesting QR -->
            <div v-else class="flex flex-col items-center gap-3 py-4">
              <div
                class="w-8 h-8 border-2 border-n-blue-9 border-t-transparent rounded-full animate-spin"
              />
              <p class="text-sm text-n-slate-9 font-medium">
                {{ t('BAILEYS.REQUESTING_QR_SHORT') }}
              </p>
            </div>
          </div>
        </div>
      </template>
    </woot-modal>
  </div>
</template>
